/* =====================================================================
   MPP round 4 (JB Digital Barossa, 28 Sep 2026)
   Keeps the free-shipping bar(s), the cart shipping line and the cart
   upsell in sync with the live cart (Shopify Cart AJAX API), and adds
   upsell products to the cart the same way Horizon's product form does,
   so the drawer / cart page re-render themselves.
   ===================================================================== */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const readConfig = () => {
  try {
    return JSON.parse(document.getElementById('mpp-cart-config')?.textContent || '{}');
  } catch {
    return {};
  }
};

const config = readConfig();
const cartUrl = () => (window.Theme?.routes?.cart_url || '/cart') + '.js';
const addUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add.js';

/** Formats cents with the shop's money format, e.g. "${{amount}}" -> "$12.50". */
function formatMoney(cents, stripZeros = false) {
  const format = config.moneyFormat || '${{amount}}';
  const value = Math.max(0, Number(cents) || 0) / 100;
  const group = (n, sep) => n.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  const withDecimals = (thousands, decimal) => {
    const [whole, frac] = value.toFixed(2).split('.');
    if (stripZeros && frac === '00') return group(whole, thousands);
    return group(whole, thousands) + decimal + frac;
  };
  const noDecimals = (thousands) => group(Math.round(value).toString(), thousands);
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return noDecimals(',');
      case 'amount_with_comma_separator':
        return withDecimals('.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return noDecimals('.');
      case 'amount_with_apostrophe_separator':
        return withDecimals("'", '.');
      default:
        return withDecimals(',', '.');
    }
  });
}

/** @param {{ total_price: number, item_count: number, currency: string, requires_shipping: boolean }} cart */
function render(cart) {
  if (!cart || typeof cart.total_price !== 'number') return;
  const threshold = Number(config.threshold) || 0;
  const sameCurrency = !config.shopCurrency || !cart.currency || cart.currency === config.shopCurrency;
  const total = cart.total_price;
  const remaining = Math.max(0, threshold - total);
  const pct = threshold > 0 ? Math.min(100, Math.round((total * 100) / threshold)) : 0;
  const state = cart.item_count === 0 ? 'empty' : remaining === 0 ? 'done' : 'progress';
  const template = state === 'empty' ? config.msgEmpty : state === 'done' ? config.msgDone : config.msgProgress;
  const message = String(template || '')
    .replaceAll('[threshold]', formatMoney(threshold, true))
    .replaceAll('[remaining]', formatMoney(remaining));

  for (const bar of document.querySelectorAll('[data-mpp-ship]')) {
    bar.hidden = !sameCurrency || threshold <= 0;
    if (bar.dataset.state !== state) bar.dataset.state = state;
    const text = bar.querySelector('[data-mpp-ship-text]');
    if (text && text.textContent !== message) text.textContent = message;
    const track = bar.querySelector('[role="progressbar"]');
    track?.setAttribute('aria-valuenow', String(pct));
    bar.querySelector('.mpp-ship__fill')?.style.setProperty('--mpp-ship-fill', String(pct / 100));
  }

  const free = threshold > 0 && cart.item_count > 0 && total >= threshold;
  for (const est of document.querySelectorAll('[data-mpp-ship-est]')) {
    est.dataset.state = free ? 'free' : 'paid';
    const value = est.querySelector('[data-mpp-ship-est-value]');
    const note = est.querySelector('[data-mpp-ship-est-note]');
    const nextValue = free ? 'FREE' : config.estFromLabel || '';
    const nextNote = free ? config.estNoteFree || '' : config.estNote || '';
    if (value && value.textContent !== nextValue) value.textContent = nextValue;
    if (note && note.textContent !== nextNote) note.textContent = nextNote;
  }
}

let pending = null;
async function fetchCart() {
  if (pending) return pending;
  pending = fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .finally(() => {
      pending = null;
    });
  return pending;
}

async function refresh() {
  try {
    render(await fetchCart());
  } catch (error) {
    console.warn('[mpp-cart] refresh failed', error);
  }
}

/** Re-sync after any cart change the theme (or an app via Shopify.actions) announces. */
function onCartChange(event) {
  const settle = () => requestAnimationFrame(() => refresh());
  if (event?.promise) {
    event.promise.then(settle, settle);
  } else {
    settle();
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartChange);
if (StandardEvents.cartDiscountUpdate) document.addEventListener(StandardEvents.cartDiscountUpdate, onCartChange);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refresh();
});

/* ---------------- Upsell: one-tap add ---------------- */
async function addUpsell(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.getAttribute('aria-busy') === 'true') return;

  const wrapper = button.closest('[data-mpp-upsell]');
  const errorEl = wrapper?.querySelector('[data-mpp-upsell-error]');
  const label = button.querySelector('[data-mpp-upsell-label]');
  if (errorEl) errorEl.hidden = true;
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  if (label) label.textContent = 'Adding…';

  const sectionIds = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId)
    ),
  ];

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  const restore = () => {
    button.removeAttribute('aria-busy');
    button.disabled = false;
    if (label) label.textContent = 'Add';
  };

  try {
    const response = await fetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
    });
    const result = await response.json();
    const cart = await fetchCart();

    if (!response.ok || result.status) {
      const message = result.description || result.message || 'Sorry, that could not be added.';
      button.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart?.items, source: 'mpp-upsell', itemCount: 1 },
      });
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      restore();
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'mpp-upsell',
        itemCount: 1,
        sections: result.sections,
        didError: false,
      },
    });
    render(cart);
    // The drawer / cart page re-render from `sections`; if this button is still here afterwards
    // (e.g. the section could not be re-rendered), put it back to a usable state.
    setTimeout(() => {
      if (button.isConnected) restore();
    }, 1500);
  } catch (error) {
    console.error('[mpp-cart] upsell add failed', error);
    deferred.reject(error);
    button.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    if (errorEl) {
      errorEl.textContent = 'Sorry, something went wrong. Please try again.';
      errorEl.hidden = false;
    }
    restore();
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-mpp-upsell-add]') : null;
  if (!target) return;
  event.preventDefault();
  addUpsell(/** @type {HTMLButtonElement} */ (target));
});
